import { CheckCircleIcon, WarningIcon } from '@/components/icons';
import { formatDateTime, formatRelativeTime } from '@/utils/date';
import type { StatsCollector } from '@/types/stats';
import './stats.css';

export interface CollectorsStripProps {
  collectors: StatsCollector[];
}

/**
 * Why a collector needs attention, or null when it is healthy
 */
function collectorProblem(collector: StatsCollector): string | null {
  if (collector.last_error) return collector.stale ? 'Stale' : 'Error';
  if (collector.stale) return 'Stale';
  return null;
}

/**
 * Per-node state of the statistics collector: when the node last reported
 * successfully, with a warning when it is stale or the last pull failed
 */
export function CollectorsStrip({ collectors }: CollectorsStripProps) {
  return (
    <section className="collectors-strip" aria-label="Collectors">
      <span className="collectors-strip-title">Collectors</span>
      {collectors.length === 0 ? (
        <span className="collectors-strip-empty">No node reports statistics yet.</span>
      ) : (
        <ul className="collectors-strip-list">
          {collectors.map((collector) => {
            const problem = collectorProblem(collector);
            const lastSuccess = formatRelativeTime(collector.last_success_at);
            const details = [
              `Last success: ${formatDateTime(collector.last_success_at, 'never')}`,
              `Last pull: ${formatDateTime(collector.last_pull_at, 'never')}`,
              collector.last_error ? `Error: ${collector.last_error}` : '',
            ]
              .filter(Boolean)
              .join('\n');

            return (
              <li
                key={collector.node_id}
                className={`collector ${problem ? 'collector-problem' : 'collector-ok'}`}
                title={details}
              >
                {problem ? <WarningIcon /> : <CheckCircleIcon size={14} />}
                <span className="collector-name">{collector.node_name}</span>
                <span className="collector-time">{lastSuccess}</span>
                {problem && <span className="collector-badge">{problem}</span>}
                {collector.last_error && <span className="collector-error">{collector.last_error}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
